import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';
import FeaturedSupervisors from '../components/FeaturedSupervisors';
import {
  Briefcase, Users, ClipboardCheck, FileText, Brain, Search, ArrowRight,
  Heart, UserPlus, Bell, User, GraduationCap, ShieldCheck, Library,
  Wifi, IdCard, Bookmark, Sparkles, FileSignature,
} from 'lucide-react';

const HERO_IMG = '/hero-inasw-flyer-v9.png';

const OFFERINGS = [
  { Icon: Briefcase, title: 'Find a Job', desc: 'Full-time, part-time, PRN, contract & remote jobs.', cta: 'Browse Jobs', to: '/jobs' },
  { Icon: Users, title: 'Supervision & Internships', desc: 'Find a supervisor or internship and track your hours.', cta: 'Explore Options', to: '/find-a-supervisor' },
  { Icon: ClipboardCheck, title: 'Licensing & Renewal Center', desc: 'State requirements, renewal info, forms, and verification links.', cta: 'View Licensing Info', to: '/license-renewal-center' },
  { Icon: Users, title: 'Community Resource Directory', desc: 'Find local resources for your clients in all 50 states & DC.', cta: 'Find Resources', to: '/community/resources' },
  { Icon: Heart, title: 'National Parent Resource Center', desc: 'Pregnancy, baby essentials, diapers, food & postpartum support for families.', cta: 'Help Families', to: '/parent-resource-center' },
  { Icon: FileText, title: 'Advance Directives & Legal Forms', desc: 'State-specific forms and instructions all in one place.', cta: 'View Forms', to: '/advance-directives' },
  { Icon: Brain, title: 'Behavioral Health Forms & Assessments', desc: 'Standardized tools for screenings and assessments.', cta: 'Explore Tools', to: '/behavioral-health-directory' },
  { Icon: FileText, title: 'Social Work Forms Center', desc: 'Printable intake, assessment & case-management templates.', cta: 'Browse Forms', to: '/social-work-forms' },
];

const POPULAR = [
  { label: 'Remote Jobs', Icon: Wifi, to: '/jobs?remote_type=remote' },
  { label: 'LCSW Jobs', Icon: Users, to: '/jobs?license=LCSW' },
  { label: 'LMSW Jobs', Icon: Users, to: '/jobs?license=LMSW' },
  { label: 'Internships', Icon: GraduationCap, to: '/internships' },
  { label: 'TX Licensure', Icon: IdCard, to: '/license-renewal-center' },
  { label: 'Supervisors', Icon: Users, to: '/find-a-supervisor' },
];

const STATS = [
  { Icon: Users, big: 'Built for Social Workers', small: 'By Social Workers' },
  { Icon: Heart, big: '495', small: 'Community Resources & Growing', key: 'resources' },
  { Icon: ShieldCheck, big: '50', small: 'States & DC Covered' },
  { Icon: Briefcase, big: 'Thousands', small: 'of Jobs Posted Every Month', key: 'jobs' },
  { Icon: ShieldCheck, big: 'Trusted by', small: 'Employers & Professionals' },
];

const LINK_COLUMNS = [
  {
    header: 'For Social Workers', tone: 'text-primary', btn: 'Find a Job', btnClass: 'bg-primary hover:bg-primary/90 text-white', to: '/jobs',
    items: [
      { Icon: Briefcase, title: 'Browse Jobs', sub: 'Full-time, part-time, PRN, contract & remote', to: '/jobs' },
      { Icon: Sparkles, title: 'Express Apply', sub: 'Quick & easy applications', to: '/jobs' },
      { Icon: Bell, title: 'Job Alerts', sub: 'Get notified about new jobs', to: '/jobs' },
      { Icon: User, title: 'Build Your Profile', sub: 'Showcase your experience', to: '/register' },
    ],
  },
  {
    header: 'Professional Resources', tone: 'text-secondary', btn: 'Explore Resources', btnClass: 'bg-secondary hover:bg-secondary-hover text-white', to: '/community/resources',
    items: [
      { Icon: Users, title: 'Community Resource Directory', sub: 'Local resources for your clients', to: '/community/resources' },
      { Icon: Heart, title: 'National Parent Resource Center', sub: 'Pregnancy, baby, food & postpartum help for families', to: '/parent-resource-center' },
      { Icon: FileText, title: 'Advance Directives & Legal Forms', sub: 'State-specific forms & instructions', to: '/advance-directives' },
      { Icon: Brain, title: 'Behavioral Health Forms & Assessments', sub: 'Standardized screenings & tools', to: '/behavioral-health-directory' },
      { Icon: FileSignature, title: 'Social Work Forms Center', sub: 'Intake, assessment & case-management templates', to: '/social-work-forms' },
      { Icon: Bookmark, title: 'My INASW Library', sub: 'Save & organize everything in one place', to: '/my-library' },
    ],
  },
  {
    header: 'Grow Your Career', tone: 'text-primary', btn: 'Advance Your Career', btnClass: 'bg-primary hover:bg-primary/90 text-white', to: '/find-a-supervisor',
    items: [
      { Icon: Users, title: 'Find a Supervisor', sub: 'In-person or remote supervision', to: '/find-a-supervisor' },
      { Icon: GraduationCap, title: 'Find an Internship', sub: 'Find field placements & track hours', to: '/internships' },
      { Icon: ClipboardCheck, title: 'Licensing & Renewal Center', sub: 'Requirements, renewal & verification', to: '/license-renewal-center' },
      { Icon: Library, title: 'CE & Professional Tools', sub: 'Grow your skills & stay current', to: '/resources' },
    ],
  },
];

const LandingNew = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [jobStat, setJobStat] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content-stats`)
      .then((r) => r.json())
      .then((d) => setJobStat(d))
      .catch(() => {});
  }, []);

  const liveStats = STATS.map((s) => {
    if (s.key === 'jobs') {
      if (jobStat && typeof jobStat.active_jobs_total === 'number') {
        const d = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return { ...s, big: jobStat.active_jobs_total.toLocaleString(), small: `Active Jobs Posted — as of ${d}` };
      }
      return { ...s, big: '—', small: 'Active Jobs Posted' };
    }
    if (s.key === 'resources') {
      if (jobStat && typeof jobStat.resources_total === 'number') {
        return { ...s, big: jobStat.resources_total.toLocaleString(), small: 'Community Resources & Growing' };
      }
      return s;
    }
    return s;
  });

  const countDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const jobCount = jobStat && typeof jobStat.active_jobs_total === 'number' ? jobStat.active_jobs_total : null;

  const runSearch = () => {
    const routes = {
      jobs: '/jobs', resources: '/community/resources', forms: '/advance-directives',
      assessments: '/behavioral-health-directory', internships: '/internships', supervisors: '/find-a-supervisor',
      licensing: '/license-renewal-center',
    };
    const base = routes[cat] || '/jobs';
    navigate(q.trim() ? `${base}?search=${encodeURIComponent(q.trim())}` : base);
  };

  return (
    <div className="bg-[#F7F5EF]" data-testid="landing-new">
      <Seo
        exactTitle="I Need A Social Worker — Nationwide Social Work Career & Recruiting Platform"
        description="I Need A Social Worker (INASW) is the nationwide social work career and recruiting platform — social work jobs, employer recruiting, internships, clinical supervision, licensing information and professional resources."
        path="/"
      />
      {/* ===== HERO ===== */}
      <section className="relative pt-24 lg:pt-28 pb-10 bg-gradient-to-b from-white to-[#F7F5EF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* SEO-friendly heading (the approved flyer below carries the visual branding) */}
          <h1 className="sr-only">I Need A Social Worker — the Nationwide Social Work Career &amp; Recruiting Platform for social work jobs, employers, internships, supervision, licensing and professional resources.</h1>
          {/* Tagline above the business name */}
          <p className="text-center font-serif text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-5" data-testid="hero-tagline">
            Everything A Social Worker Needs, All In One Place.
          </p>

          {/* Approved flyer — displayed EXACTLY as provided (uncropped), centered & responsive. Baked-in icons are real clickable links. */}
          <div className="relative w-full max-w-5xl mx-auto" data-testid="hero-banner">
            <img src={HERO_IMG} alt="I Need A Social Worker — the nationwide platform for social work jobs, employers, internships, supervision, licensing, community resources and professional tools. Powered by Diversified Social Work Solutions." className="w-full h-auto block rounded-xl" data-testid="hero-image" />
            {[
              { to: '/jobs', label: 'Find a Job', left: '3%', width: '18%' },
              { to: '/for-employers', label: 'Find a Social Work Candidate', left: '22%', width: '18%' },
              { to: '/find-a-supervisor', label: 'Find a Supervisor', left: '42%', width: '17%' },
              { to: '/internships', label: 'Find an Internship', left: '61%', width: '15%' },
              { to: '/community/resources', label: 'Community Resources', left: '77%', width: '20%' },
            ].map((z, i) => (
              <Link key={i} to={z.to} aria-label={z.label} title={z.label}
                data-testid={`hero-hotspot-${i}`}
                className="absolute rounded-lg hover:bg-secondary/15 hover:ring-2 hover:ring-secondary/40 transition-colors"
                style={{ left: z.left, width: z.width, top: '66%', height: '15%' }} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== POSITIONING TAGLINE + TWO PATHS (SEO context: career + recruiting platform) ===== */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 mb-4 text-center" data-testid="positioning-tagline">
        <h2 className="font-serif text-xl md:text-2xl font-bold text-primary leading-snug py-1">
          The Nationwide Social Work Career &amp; Recruiting Platform
        </h2>
        <p className="mt-2 text-sm md:text-base text-gray-600 max-w-3xl mx-auto">
          <span className="font-semibold text-primary">Employers:</span> hire qualified social workers.{' '}
          <span className="font-semibold text-primary">Social workers:</span> find jobs, internships, clinical supervisors, licensing information and professional resources.
        </p>
        <Link to="/about" data-testid="brand-link-home" className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-secondary hover:gap-2 transition-all">
          What is I Need A Social Worker? <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-2" data-testid="two-paths">
        <div className="grid md:grid-cols-2 gap-4">
          {/* For Employers */}
          <div className="rounded-2xl bg-primary text-white p-6 flex flex-col" data-testid="path-employers">
            <p className="text-secondary font-bold text-xs uppercase tracking-widest">For Employers</p>
            <h3 className="font-serif text-2xl font-bold mt-1">Hire Social Workers</h3>
            <p className="text-white/80 text-sm mt-2 flex-1">Post Jobs • Search Candidates • Recruit Licensed Professionals</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Link to="/employer/post-job"><Button className="bg-secondary hover:bg-secondary-hover text-white h-10 px-5" data-testid="path-employer-post">Post a Job <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              <Link to="/for-employers"><Button variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20 h-10 px-5" data-testid="path-employer-learn">Hire Social Workers</Button></Link>
            </div>
          </div>
          {/* For Social Workers */}
          <div className="rounded-2xl bg-white border-2 border-primary/15 p-6 flex flex-col" data-testid="path-social-workers">
            <p className="text-secondary font-bold text-xs uppercase tracking-widest">For Social Workers</p>
            <h3 className="font-serif text-2xl font-bold mt-1 text-primary">Find Jobs &amp; Grow Your Career</h3>
            <p className="text-gray-600 text-sm mt-2 flex-1">Jobs • Internships • Clinical Supervisors • Resources • Licensing • Private Practice</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Link to="/jobs"><Button className="bg-primary hover:bg-primary/90 text-white h-10 px-5" data-testid="path-sw-jobs">Find a Job <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              <Link to="/find-a-supervisor"><Button variant="outline" className="border-primary/30 text-primary hover:bg-primary/5 h-10 px-5" data-testid="path-sw-more">Supervision &amp; More</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== LIVE JOB COUNT CARD (same eligibility as the Jobs page; two honest numbers) ===== */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 -mt-1 mb-2" data-testid="live-job-count-section">
        <div className="relative bg-[#F7F5EF] border-2 border-primary/25 rounded-2xl px-6 py-7 text-center shadow-sm">
          <Link to="/jobs" data-testid="job-count-header" aria-label="Browse all social work jobs"
            className="inline-block bg-[#A8322A] hover:bg-[#8f2a23] text-white font-extrabold tracking-wide text-lg md:text-2xl px-10 py-4 rounded-xl shadow-md hover:shadow-lg cursor-pointer uppercase transition-all hover:-translate-y-0.5">
            Social Work Jobs
          </Link>
          <div className="mt-3 font-serif font-black text-primary leading-none text-6xl md:text-7xl tabular-nums" data-testid="live-job-count">
            {jobCount !== null ? jobCount.toLocaleString() : '—'}
          </div>
          <div className="mt-2 text-[#A8322A] font-extrabold tracking-wide text-base md:text-xl uppercase">
            Active Social Work Jobs Nationwide
          </div>
          <div className="mt-3 inline-block bg-secondary text-white font-bold tracking-wide text-xs md:text-sm px-5 py-1.5 rounded-md uppercase">
            Updated Daily at 8:00 AM
          </div>
          <div className="mt-3 font-serif font-bold text-primary text-sm md:text-base" data-testid="job-count-date">{countDate}</div>
          <Link to="/jobs" data-testid="job-count-view-all" className="mt-3 inline-flex items-center gap-1 text-secondary font-semibold text-sm hover:gap-2 transition-all">
            View all jobs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ===== EMPLOYER INVITATION — prominent, near the top ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6" data-testid="employer-invite-top">
        <div className="relative overflow-hidden rounded-2xl bg-primary text-white px-6 py-6 md:px-8 md:py-7 flex flex-col md:flex-row md:items-center gap-5">
          <div className="absolute -right-8 -top-10 w-40 h-40 rounded-full bg-secondary/20 pointer-events-none" />
          <div className="flex-1 relative">
            <p className="text-secondary font-bold text-xs uppercase tracking-widest">Employers &amp; Organizations</p>
            <h2 className="font-serif text-2xl md:text-3xl font-bold mt-1 leading-tight">Hiring social workers? Reach licensed, qualified candidates nationwide.</h2>
            <p className="text-white/80 text-sm md:text-base mt-2 max-w-2xl">Post a job in minutes, search candidates, and manage applicants — on a platform built specifically for the social work profession.</p>
          </div>
          <div className="relative flex flex-col sm:flex-row md:flex-col gap-2 w-full md:w-auto flex-shrink-0">
            <Link to="/employer/post-job" className="w-full"><Button className="w-full bg-secondary hover:bg-secondary-hover text-white h-11 px-6 justify-between" data-testid="employer-invite-post">Post a Job <ArrowRight className="w-4 h-4" /></Button></Link>
            <Link to="/for-employers" className="w-full"><Button variant="outline" className="w-full bg-white/10 border-white/40 text-white hover:bg-white/20 h-11 px-6" data-testid="employer-invite-plans">See Employer Plans</Button></Link>
          </div>
        </div>
      </section>

      {/* ===== FEATURED VERIFIED SUPERVISORS (rotating; verified-only) ===== */}
      <FeaturedSupervisors />

      {/* ===== EXPLORE WHAT INASW OFFERS ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-testid="offerings-section">
        <h2 className="text-center font-serif text-2xl md:text-3xl font-bold text-primary mb-8">
          <span className="text-secondary">—</span> Explore What INASW Offers <span className="text-secondary">—</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {OFFERINGS.map((o) => (
            <Link key={o.title} to={o.to} className="bg-white rounded-2xl border p-5 flex flex-col items-center text-center hover:shadow-lg hover:border-secondary transition-all group" data-testid={`offering-${o.to.replace(/\//g, '')}`}>
              <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center mb-3 group-hover:bg-primary/10"><o.Icon className="w-6 h-6 text-primary" /></div>
              <h3 className="font-serif font-bold text-primary text-base leading-tight">{o.title}</h3>
              <p className="text-xs text-gray-500 mt-2 flex-1">{o.desc}</p>
              <span className="text-xs font-semibold text-secondary mt-3 flex items-center gap-1">{o.cta} <ArrowRight className="w-3 h-3" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* CTAs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/jobs" data-testid="hero-find-jobs">
            <Button className="bg-primary hover:bg-primary/90 text-white h-auto py-3 px-6 flex-col items-start rounded-xl">
              <span className="flex items-center gap-2 font-semibold text-base"><Briefcase className="w-4 h-4" /> Find Social Work Jobs</span>
              <span className="text-xs font-normal text-white/75">Browse Jobs or Express Apply</span>
            </Button>
          </Link>
          <Link to="/register" data-testid="hero-get-started">
            <Button variant="outline" className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-white h-auto py-3 px-6 flex-col items-start rounded-xl bg-white">
              <span className="flex items-center gap-2 font-semibold text-base"><UserPlus className="w-4 h-4" /> Get Started Free</span>
              <span className="text-xs font-normal opacity-75">Join Social Workers Nationwide</span>
            </Button>
          </Link>
        </div>
      </section>

      {/* ===== SEARCH ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-primary rounded-2xl p-6 shadow-lg" data-testid="platform-search">
          <h2 className="text-center text-white font-serif text-xl md:text-2xl font-bold mb-4">Search Across the Entire Platform</h2>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                placeholder="Search jobs, forms, assessments, resources, internships, supervisors, licensing info…"
                className="w-full h-12 rounded-lg pl-9 pr-3 text-sm border-0" data-testid="platform-search-input" />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-12 rounded-lg px-3 text-sm border-0 md:w-48 bg-white" data-testid="platform-search-category">
              <option value="all">All Categories</option>
              <option value="jobs">Jobs</option>
              <option value="resources">Community Resources</option>
              <option value="forms">Legal Forms</option>
              <option value="assessments">Behavioral Health</option>
              <option value="internships">Internships</option>
              <option value="supervisors">Supervisors</option>
              <option value="licensing">Licensing</option>
            </select>
            <Button onClick={runSearch} className="h-12 px-8 bg-secondary hover:bg-secondary-hover text-white" data-testid="platform-search-btn"><Search className="w-4 h-4 mr-2" /> Search</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-white/70 text-xs font-semibold">Popular Searches:</span>
            {POPULAR.map((p) => (
              <button key={p.label} onClick={() => navigate(p.to)} className="flex items-center gap-1.5 text-xs text-white/90 border border-white/25 rounded-full px-3 py-1.5 hover:bg-white/10" data-testid={`popular-${p.label.replace(/\s+/g, '-').toLowerCase()}`}>
                <p.Icon className="w-3.5 h-3.5" /> {p.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="mt-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary rounded-2xl px-6 py-6 grid grid-cols-2 md:grid-cols-5 gap-6" data-testid="stats-bar">
            {liveStats.map((s, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`stat-${s.key || i}`}>
                <s.Icon className="w-8 h-8 text-secondary flex-shrink-0" />
                <div><p className="text-white font-bold leading-tight">{s.big}</p><p className="text-white/70 text-xs">{s.small}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== JOIN FREE ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="bg-secondary/10 border border-secondary/20 rounded-2xl p-6 grid md:grid-cols-3 gap-6 items-center">
          <div className="flex items-start gap-3">
            <User className="w-9 h-9 text-primary flex-shrink-0" />
            <div><h3 className="font-serif font-bold text-primary text-lg">Join Free as a Social Worker</h3><p className="text-sm text-gray-600 mt-1">Create your free account to save jobs, forms, resources, and track your career in one place.</p></div>
          </div>
          <div className="text-center">
            <Link to="/register"><Button className="bg-secondary hover:bg-secondary-hover text-white px-8 h-11" data-testid="join-signup-free">Sign Up Free</Button></Link>
            <p className="text-xs text-gray-500 mt-2 font-medium">It's Fast, Free &amp; Easy!</p>
          </div>
          <div className="flex items-start gap-3">
            <Heart className="w-9 h-9 text-primary flex-shrink-0" />
            <div><h3 className="font-serif font-bold text-primary text-lg">Inclusive &amp; Affirming</h3><p className="text-sm text-gray-600 mt-1">Find jobs and resources serving every community — Deaf &amp; Hard of Hearing, LGBTQ+, Veterans, older adults, children &amp; families and more.</p><Link to="/jobs" className="text-sm text-primary hover:text-secondary font-medium">Explore by community served →</Link></div>
          </div>
        </div>
      </section>

      {/* ===== LINK COLUMNS ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {LINK_COLUMNS.map((col) => (
            <div key={col.header} className="bg-white rounded-2xl border p-5 flex flex-col">
              <h3 className={`font-serif font-bold text-lg mb-4 ${col.tone}`}>{col.header}</h3>
              <ul className="space-y-3 flex-1">
                {col.items.map((it) => (
                  <li key={it.title}>
                    <Link to={it.to} className="flex items-start gap-2 group">
                      <it.Icon className="w-4 h-4 text-gray-400 group-hover:text-secondary flex-shrink-0 mt-0.5" />
                      <span><span className="block text-sm font-semibold text-gray-800 group-hover:text-primary leading-tight">{it.title}</span><span className="block text-xs text-gray-400">{it.sub}</span></span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to={col.to}><Button className={`w-full mt-4 justify-center ${col.btnClass}`}>{col.btn} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
            </div>
          ))}
        </div>
      </section>

      {/* ===== BOTTOM TRIO ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 mb-16">
        <div className="grid md:grid-cols-3 gap-6 border-t pt-8">
          <div className="flex items-start gap-3"><Heart className="w-8 h-8 text-primary flex-shrink-0" /><div><h4 className="font-serif font-bold text-primary">YOU ARE NOT ALONE.</h4><p className="text-sm text-gray-600">Together, we can build stronger communities and brighter futures.</p></div></div>
          <div className="flex items-start gap-3"><Bookmark className="w-8 h-8 text-secondary flex-shrink-0" /><div><Link to="/my-library" className="font-serif font-bold text-primary hover:text-secondary">My INASW Library</Link><p className="text-sm text-gray-600">Save jobs, forms, assessments, and resources in one place.</p></div></div>
          <div className="flex items-start gap-3"><Users className="w-8 h-8 text-primary flex-shrink-0" /><div><h4 className="font-serif font-bold text-primary">Start Exploring Today!</h4><p className="text-sm text-gray-600">Join thousands of social workers who trust INASW.</p></div></div>
        </div>
      </section>
    </div>
  );
};

export default LandingNew;
